import { FUNNEL_SIGNAL_RETENTION_DAYS } from "@repo/shared";

import { CookiePreferencesButton } from "~/components/consent/CookiePreferencesButton";
import { MeasurementOptOut, OPT_OUT_LABEL } from "~/components/consent/MeasurementOptOut";

import { LegalSection } from "../LegalPage";

import {
  OPERATOR_DELAWARE_FILE_NUMBER,
  OPERATOR_LEGAL_ADDRESS,
  OPERATOR_LEGAL_NAME,
  OPERATOR_REGISTERED_AGENT,
} from "./operator";

/** Fecha de la versión vigente. La muestran /privacy y el diálogo del checkbox de registro. */
export const PRIVACY_UPDATED_AT = "8 de octubre de 2026";

/**
 * El texto de la Política de Privacidad, UNA sola vez: lo usan la página /privacy y el diálogo
 * de la casilla "Acepto los Términos… y la Política de Privacidad". Ver `TermsContent`.
 *
 * Describe lo que el código hace de verdad (lib/tracking, server-attribution.js, módulo
 * tracking de la API). Si se suma o se saca una plataforma, esto se actualiza en el MISMO
 * cambio. Los datos legales no se inventan: sólo va lo que el responsable confirmó.
 *
 * Variante ÚNICA: la del aviso de cookies apagado (`TRACKING_CONSENT_BANNER` vacío), que mide
 * desde la primera visita y ofrece `OPT_OUT_LABEL`. Si algún día se prende el aviso,
 * este texto se reescribe en ese mismo cambio.
 */

type CookieRow = { name: string; who: string; purpose: string; duration: string };

/** Adenda de privacidad de Turnstile: la publica Cloudflare, no la redactamos nosotros. */
export const TURNSTILE_PRIVACY_URL = "https://www.cloudflare.com/turnstile-privacy-policy/";

/** Días que se guarda el registro de seguridad de los ingresos (AUTH_ATTEMPTS_RETENTION_DAYS). */
const LOGIN_LOG_RETENTION_DAYS = 180;

/** Días que se guarda el registro propio de visitas (SITE_VISITS_RETENTION_DAYS). */
export const VISIT_LOG_RETENTION_DAYS = 180;

/**
 * Días que guardamos el identificador de la grabación de Clarity junto al registro de visitas
 * (CLARITY_LINK_RETENTION_DAYS de la API; un test controla que coincidan).
 */
export const CLARITY_LINK_RETENTION_DAYS = 90;

/** Días tras los que se borran la IP y el navegador de ese registro (SITE_VISITS_PII_DAYS). */
export const VISIT_LOG_PII_DAYS = 30;

/** Días aproximados que guarda el servidor web sus registros técnicos (Caddy, roll_keep_for). */
const WEB_SERVER_LOG_DAYS = 30;

const COOKIES: { title: string; note?: string; rows: CookieRow[] }[] = [
  {
    title: "Necesarias (siempre activas)",
    rows: [
      {
        name: "access_token, refresh_token",
        who: "AcademIA",
        purpose: "Mantener tu sesión iniciada de forma segura.",
        duration: "Hasta 30 días",
      },
      {
        name: "acd_consent",
        who: "AcademIA",
        purpose:
          "Recordar si elegiste no usar herramientas de medición ni publicidad en este navegador.",
        duration: "12 meses",
      },
    ],
  },
  {
    title: "Propias de medición",
    note: "Quedan en AcademIA. Sirven para saber de qué campaña llegaste y unir esa visita con tu registro.",
    rows: [
      {
        name: "acd_attr",
        who: "AcademIA",
        purpose:
          "Desde qué campaña o sitio llegaste (parámetros utm y el identificador del clic del anuncio).",
        duration: "90 días",
      },
      {
        name: "acd_aid",
        who: "AcademIA",
        purpose: "Un identificador al azar de tu navegador, para unir esa visita con tu registro.",
        duration: "12 meses",
      },
    ],
  },
  {
    title: "Analítica",
    rows: [
      {
        name: "_ga, _ga_*",
        who: "Google Analytics",
        purpose: "Contar visitas y páginas vistas.",
        duration: "2 años",
      },
      {
        name: "ph_*",
        who: "PostHog",
        purpose: "Entender cómo se usa el sitio y dónde se traba la gente.",
        duration: "12 meses",
      },
      {
        name: "_clck, _clsk",
        who: "Microsoft Clarity",
        purpose: "Mapas de calor y grabaciones de uso, con lo que escribís oculto.",
        duration: "12 meses / 1 día",
      },
    ],
  },
  {
    title: "Publicidad",
    rows: [
      {
        name: "_gcl_*",
        who: "Google Ads",
        purpose: "Saber qué anuncio de Google te trajo.",
        duration: "90 días",
      },
      {
        name: "_fbp, _fbc",
        who: "Meta (Facebook e Instagram)",
        purpose: "Saber qué anuncio de Meta te trajo.",
        duration: "90 días",
      },
      {
        name: "_ttp",
        who: "TikTok",
        purpose: "Saber qué anuncio de TikTok te trajo.",
        duration: "13 meses",
      },
      {
        name: "personalization_id",
        who: "X",
        purpose: "Saber qué anuncio de X te trajo.",
        duration: "2 años",
      },
      {
        name: "li_fat_id",
        who: "LinkedIn",
        purpose: "Saber qué anuncio de LinkedIn te trajo.",
        duration: "30 días",
      },
    ],
  },
];

/** Rótulo de cada dato de la ficha de una cookie: sólo en el teléfono, donde no hay encabezado. */
function MobileLabel({ children }: { children: string }) {
  return <span className="font-semibold text-landing-muted sm:hidden">{children}: </span>;
}

/**
 * `inDialog`: dentro del diálogo de la casilla de términos no van ni el botón de preferencias (el
 * aviso de cookies quedaría detrás del diálogo modal, sin poder usarse) ni `MeasurementOptOut`
 * (se usa desde /privacy).
 */
export function PrivacyContent({ inDialog = false }: { inDialog?: boolean }) {
  return (
    <>
      <LegalSection title="1. Responsable">
        <p>
          El responsable del tratamiento de tus datos es {OPERATOR_LEGAL_NAME}, una sociedad de
          responsabilidad limitada constituida en el Estado de Delaware, Estados Unidos (número de
          registro {OPERATOR_DELAWARE_FILE_NUMBER}), con domicilio legal en {OPERATOR_LEGAL_ADDRESS}{" "}
          (agente registrado: {OPERATOR_REGISTERED_AGENT}). AcademIA (cursosacademia.ar) es una
          marca de {OPERATOR_LEGAL_NAME}. Contacto:{" "}
          <a className="font-bold text-landing-red" href="mailto:hola@cursosacademia.ar">
            hola@cursosacademia.ar
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="2. Qué datos usamos">
        <ul>
          <li>
            <b>Datos de cuenta:</b> nombre, apellido y número de teléfono (WhatsApp), que nos das al
            registrarte o pre-inscribirte.
          </li>
          <li>
            <b>Datos académicos:</b> cursos en los que estás inscripto o pre-inscripto, tu progreso,
            resultados de ejercicios y evaluaciones, certificados emitidos y lo que le escribís al
            mentor con IA de cada curso.
          </li>
          <li>
            <b>Datos de uso y técnicos:</b> información del dispositivo y del navegador, dirección
            IP y páginas visitadas, necesarias para que la plataforma funcione y para mejorarla.
          </li>
          <li>
            <b>Datos de campañas:</b> desde qué anuncio, campaña o sitio llegaste (parámetros utm e
            identificadores de clic como gclid, fbclid o ttclid), la página por la que entraste y un
            identificador al azar de tu navegador.
          </li>
          <li>
            <b>Registro propio de visitas:</b> cada vez que se abre una página del sitio, nuestro
            servidor anota la fecha y hora, la página, de dónde llegaste (si se sabe), el país y la
            red aproximados según tu IP, el tipo de navegador y dispositivo, y si la página llegó a
            mostrarse, y si tocaste los botones para inscribirte o registrarte. También guarda tu
            dirección IP y los datos de tu navegador, que se borran a los {VISIT_LOG_PII_DAYS} días.
            Lo usamos para contar visitas reales y separar robots y precargas. No depende de cookies
            de terceros y funciona aunque elijas no usar las herramientas de medición y publicidad
            (sección 5).
          </li>
          <li>
            <b>Registro de seguridad de los ingresos:</b> cada vez que pedís un código para entrar,
            registrarte o cambiar tu teléfono guardamos el número, la dirección IP (y el país y el
            proveedor de internet que se deducen de ella), el navegador y el dispositivo, y los
            datos de la comprobación anti-robots: su resultado y señales del formulario, como cuánto
            tardó en completarse.
          </li>
          <li>
            <b>Datos de pago:</b> los procesa Mercado Pago. No almacenamos datos de tarjetas.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="3. Para qué los usamos">
        <ul>
          <li>Crear tu cuenta y verificar tu identidad con códigos por WhatsApp.</li>
          <li>
            Frenar abusos: detectar robots y pedidos masivos de códigos, y ayudarte si no podés
            entrar. Para eso usamos el registro de seguridad de los ingresos.
          </li>
          <li>Darte acceso a los cursos y guardar tu progreso y certificados.</li>
          <li>
            Avisarte por WhatsApp lo que pediste que te avisemos: códigos de acceso, lanzamientos de
            cursos a los que te pre-inscribiste y novedades importantes de tu cuenta.
          </li>
          <li>Procesar compras y emitir comprobantes a través de los proveedores de pago.</li>
          <li>
            <b>Analítica:</b> entender cómo se usa el sitio para mejorarlo. Salvo que elijas no ser
            medido, la grabación de tu visita en Microsoft Clarity puede quedar asociada a tu cuenta
            con un identificador cifrado (no le mandamos tu nombre ni tu teléfono), para poder
            revisar un problema de ingreso o de uso. A Clarity también le pasamos la campaña por la
            que llegaste, el tipo de navegador (por ejemplo, el de TikTok) y los pasos del registro
            que completaste, nunca tu nombre, teléfono ni email; y guardamos el identificador de la
            grabación junto a tu registro de visitas durante {CLARITY_LINK_RETENTION_DAYS} días.
          </li>
          <li>
            <b>Publicidad:</b> medir qué anuncios traen gente que se registra, se inscribe o compra,
            para no gastar en publicidad que no sirve.
          </li>
        </ul>
        <p>
          Estas herramientas se activan desde tu primera visita. Si no querés que las usemos, mirá
          la sección 5.
        </p>
        <p>
          <b>No vendemos tus datos.</b> No te vamos a llenar de spam: los mensajes de WhatsApp se
          limitan a lo que pediste.
        </p>
      </LegalSection>

      <LegalSection title="4. Con quién los compartimos">
        <p>
          Sólo con los proveedores que hacen falta, y sólo lo imprescindible. Tratan los datos según
          sus propias políticas y nuestras instrucciones.
        </p>
        <ul>
          <li>
            <b>Para que la plataforma funcione:</b> Kapso (envío de mensajes de WhatsApp), Mercado
            Pago (pagos), DigitalOcean (servidores y almacenamiento), Bunny (videos de los cursos),
            Cloudflare Turnstile (comprobar que quien pide un código para entrar o registrarse no es
            un robot; ver su{" "}
            <a
              className="font-bold text-landing-red"
              href={TURNSTILE_PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              adenda de privacidad
            </a>
            ), Google Fonts (tipografías del sitio), OpenAI (respuestas del mentor con IA) y, cuando
            está activo, Sentry (reporte de errores del sitio, con los textos y lo que escribís
            ocultos).
          </li>
          <li>
            <b>Analítica:</b> Google Analytics, PostHog y Microsoft Clarity.
          </li>
          <li>
            <b>Publicidad:</b> Meta (Facebook e Instagram), TikTok, Google Ads, X y LinkedIn. Además
            de sus cookies, les avisamos desde nuestro servidor cuando ves la página de un curso (y
            si te quedaste a mirarla o tocaste «Inscribirme gratis» o «Registrate»), pedís el código
            de ingreso, te registrás, te inscribís a un curso, comprás o terminás un curso, para que
            puedan medir la campaña. Les mandamos tu teléfono, tu nombre y apellido y, si lo
            cargaste, tu email, pasados por una función de hash (SHA-256): no viajan legibles, pero
            la plataforma puede reconocerlos si ya los tiene. También les mandamos el identificador
            del anuncio en el que hiciste clic, la dirección IP y los datos del navegador de la
            visita. Nunca les mandamos tus respuestas ni lo que hacés en las clases.
          </li>
        </ul>
        <p>
          No todas estas plataformas están activas siempre: sólo se cargan las que tenemos
          configuradas.
        </p>
      </LegalSection>

      <LegalSection title="5. Cookies" id="cookies">
        <p>
          Algunas cookies son necesarias para que el sitio funcione y otras son de medición. Las de
          analítica y publicidad se cargan desde tu primera visita. Si no querés que las usemos,
          tocá «{OPT_OUT_LABEL}» acá abajo: dejamos de cargar esas herramientas en este navegador y
          de avisarles de tus visitas, registros, inscripciones y compras. El registro propio de
          visitas (sección 2) sigue funcionando. También podés bloquear las cookies de terceros
          desde la configuración de tu navegador.
        </p>
        {COOKIES.map((group) => (
          <div key={group.title} className="mt-2">
            <p className="font-semibold text-landing-text">{group.title}</p>
            {group.note && <p className="text-[13.5px] text-landing-muted">{group.note}</p>}
            {/* En un teléfono la tabla no entra (antes medía 520px fijos y abría un scroll de
                costado, también en el diálogo del registro): cada cookie pasa a ser una ficha
                con su nombre arriba y los tres datos rotulados abajo. Es el MISMO marcado — no
                hay una segunda copia del texto —, sólo cambia cómo se dibuja debajo de `sm`. */}
            <table className="mt-1.5 w-full border-collapse text-left text-[13.5px] max-sm:block sm:table-fixed">
              {/* Mismas columnas en las cuatro tablas: se leen como una sola. */}
              <colgroup>
                <col className="w-[24%]" />
                <col className="w-[20%]" />
                <col />
                <col className="w-[15%]" />
              </colgroup>
              <thead className="max-sm:hidden">
                <tr className="border-b border-landing-border text-landing-muted">
                  <th className="py-1.5 pr-3 font-semibold">Cookie</th>
                  <th className="py-1.5 pr-3 font-semibold">De quién</th>
                  <th className="py-1.5 pr-3 font-semibold">Para qué</th>
                  <th className="py-1.5 font-semibold">Duración</th>
                </tr>
              </thead>
              <tbody className="max-sm:block">
                {group.rows.map((row) => (
                  <tr
                    key={row.name}
                    className="border-b border-landing-border/60 align-top max-sm:block max-sm:py-2"
                  >
                    <td className="py-1.5 pr-3 font-mono text-[12.5px] [overflow-wrap:anywhere] max-sm:block max-sm:py-0 max-sm:font-semibold max-sm:text-landing-text">
                      {row.name}
                    </td>
                    <td className="py-1.5 pr-3 max-sm:block max-sm:py-0.5">
                      <MobileLabel>De quién</MobileLabel>
                      {row.who}
                    </td>
                    <td className="py-1.5 pr-3 max-sm:block max-sm:py-0.5">
                      <MobileLabel>Para qué</MobileLabel>
                      {row.purpose}
                    </td>
                    <td className="py-1.5 max-sm:block max-sm:py-0.5 sm:whitespace-nowrap">
                      <MobileLabel>Duración</MobileLabel>
                      {row.duration}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        <p className="text-[13.5px] text-landing-muted">
          Las duraciones de las cookies de terceros son las que fija cada plataforma por defecto y
          pueden cambiar. El tema claro u oscuro y el carrito se guardan en el almacenamiento local
          de tu navegador, no en cookies.
        </p>
        {!inDialog && (
          <div className="flex flex-col items-start gap-2">
            <MeasurementOptOut />
            {/* Sólo aparece con el aviso de cookies prendido. */}
            <CookiePreferencesButton />
          </div>
        )}
      </LegalSection>

      <LegalSection title="6. Datos fuera de Argentina">
        <p>
          Varios de estos proveedores procesan datos fuera de Argentina, por ejemplo en Estados
          Unidos o en la Unión Europea.
        </p>
      </LegalSection>

      <LegalSection title="7. Cuánto tiempo los guardamos">
        <p>
          Mientras tengas tu cuenta activa. Si pedís la baja, eliminamos o anonimizamos tus datos
          personales dentro de un plazo razonable, salvo lo que debamos conservar por obligación
          legal (por ejemplo, comprobantes de pago) o para la verificación pública de certificados
          ya emitidos.
        </p>
        <p>
          El registro de seguridad de los ingresos se guarda {LOGIN_LOG_RETENTION_DAYS} días y
          después se borra solo.
        </p>
        <p>
          El registro propio de visitas se guarda {VISIT_LOG_RETENTION_DAYS} días. La dirección IP y
          los datos del navegador de ese registro se borran antes, a los {VISIT_LOG_PII_DAYS} días.
          Los avisos a las plataformas de publicidad de que viste un curso, te quedaste a mirarlo o
          tocaste «Inscribirme gratis» (con la dirección IP y el navegador) se borran de nuestros
          servidores a los {FUNNEL_SIGNAL_RETENTION_DAYS} días. Los registros técnicos del servidor
          web (dirección IP, navegador y página pedida) se guardan unos {WEB_SERVER_LOG_DAYS} días.
        </p>
      </LegalSection>

      <LegalSection title="8. Tus derechos">
        <p>
          Conforme a la Ley 25.326 de Protección de Datos Personales (Argentina), podés acceder,
          rectificar, actualizar o suprimir tus datos escribiéndonos a{" "}
          <a className="font-bold text-landing-red" href="mailto:hola@cursosacademia.ar">
            hola@cursosacademia.ar
          </a>
          . También podés pedir en cualquier momento que no usemos herramientas de medición ni
          publicidad con «{OPT_OUT_LABEL}» (sección 5): dejamos de cargar esas herramientas y de
          avisarles de tus visitas, registros, inscripciones y compras. La Agencia de Acceso a la
          Información Pública (AAIP), como órgano de control, tiene la atribución de atender
          denuncias y reclamos sobre incumplimientos de la ley.
        </p>
      </LegalSection>

      <LegalSection title="9. Seguridad">
        <p>
          Aplicamos medidas técnicas y organizativas razonables para proteger tus datos: acceso
          cifrado (HTTPS), autenticación por código de un solo uso, y acceso restringido a la
          información. Ningún sistema es infalible; si detectamos un incidente que te afecte, te lo
          comunicaremos.
        </p>
      </LegalSection>

      <LegalSection title="10. Menores de edad">
        <p>
          La plataforma está pensada para personas mayores de 18 años. Si detectamos una cuenta de
          un menor sin consentimiento de sus responsables, la daremos de baja.
        </p>
      </LegalSection>

      <LegalSection title="11. Cambios a esta política">
        <p>
          Si cambiamos esta política de forma relevante, lo comunicaremos por la plataforma o por
          WhatsApp. La fecha de «última actualización» indica siempre la versión vigente.
        </p>
      </LegalSection>
    </>
  );
}
